import type { CSSProperties, ReactNode } from 'react';
import { useRef } from 'react';
import type { ComparisonDeckStats, PlayerProfile, GAStartingLoadout } from '@sleeved/lib';
import { StackedCardAvatars } from '../../components/PlayerWidgetParts';
import { useFitToWidth } from '../../hooks/useFitToWidth';
import {
  GA_ELEMENT_ICON_MAP,
  isGAElement,
} from '../../assets/grand-archive/elementIcons';
import {
  palette,
  glass,
  glassBorder,
  glassText,
  fonts,
} from '../../styles/overlayTokens';
import type { AnimPhase } from './chartPrimitives';
import {
  STREAM_TEXT_OUTLINE,
  p1rgba,
  p2rgba,
  makeAnimCls,
  DonutTypeChart,
  MiniBarChart,
  KeywordBubbleCloud,
  DensePanelPlaceholder,
} from './chartPrimitives';

// ---------------------------------------------------------------------------
// grandArchive — GaHighlightsRow/buildGaHighlightPills helpers, plus
// GrandArchiveDensePanel + GrandArchiveRailPanel. Split out of the former
// DeckOverlayPanels.tsx monolith (pure move, no behavior change).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// GaHighlightsRow — GA "Highlights" pill row (Spirit / Champion / Divine Relic),
// shared by GrandArchiveDensePanel and GrandArchiveRailPanel.
//
// CD directive: the row must "only ever take up one row" — full card names are
// preserved via fit-to-width font scaling (never truncated as the primary
// mechanism); per-pill ellipsis is only a last-resort fallback if content still
// overflows at the legibility floor.
//
// Measurement gotcha: once the visible row is `flexWrap:'nowrap'` with
// `minWidth:0` pills, its own `scrollWidth` never overflows — the browser just
// shrinks/ellipsizes the pills silently, so it always "fits". A hidden probe
// replica (flexShrink:0, whiteSpace:nowrap, width:max-content) is required to
// read the row's true INTRINSIC (unshrunk) width at each candidate font size.
// The fit loop itself lives in the shared `useFitToWidth` hook (also used by
// AutoShrinkText in PlayerWidgetParts.tsx) — this component supplies only the
// probe shape (multi-pill row) and the per-candidate apply/measure callbacks.
// ---------------------------------------------------------------------------

interface GaHighlightPillDatum {
  key: string;
  label: string;
  value: string;
}

/** Builds the pill list from the GA starting-loadout payload — identical logic
 *  previously inlined at both call sites (dense + rail panels). */
function buildGaHighlightPills(loadout: GAStartingLoadout | null | undefined): GaHighlightPillDatum[] {
  if (!loadout) return [];
  const pills: GaHighlightPillDatum[] = [];
  if (loadout.levelZeroChampionName != null) {
    pills.push({ key: 'spirit', label: 'SPIRIT', value: loadout.levelZeroChampionName });
  }
  if (
    loadout.highestChampionCardName != null &&
    loadout.highestChampionCardName !== loadout.levelZeroChampionName
  ) {
    pills.push({ key: 'champion', label: 'CHAMPION', value: loadout.highestChampionCardName });
  }
  pills.push({ key: 'divine-relic', label: 'DIVINE RELIC', value: loadout.divineRelicCardName ?? 'None' });
  return pills;
}

const GA_HIGHLIGHT_NAME_MAX = 14; // matches the original hardcoded card-name font size
const GA_HIGHLIGHT_NAME_MIN = 10; // legibility floor for the card-name text
const GA_HIGHLIGHT_LABEL_RATIO = 12 / 14; // preserve the 12:14 label:name ratio while scaling
const GA_HIGHLIGHT_FIT_STEP = 0.5; // px decrement per iteration of the fit loop

interface GaHighlightsRowProps {
  pills: GaHighlightPillDatum[];
  isP1Layout: boolean;
  eyebrowStyle: CSSProperties;
  marginTop: number;
  animClassName: string;
}

function GaHighlightsRow({ pills, isP1Layout, eyebrowStyle, marginTop, animClassName }: GaHighlightsRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const probeRowRef = useRef<HTMLDivElement>(null);

  // Cheap identity key so the fit effect re-runs when the loadout content changes
  // (e.g. a different deck is loaded) without depending on the `pills` array reference.
  const pillsKey = pills.map(p => `${p.key}:${p.value}`).join('|');

  // Start at full size — the hook's internal useLayoutEffect shrinks (if needed) before paint,
  // so there's no first-render flash at the un-fitted size. If nothing fits even at the floor,
  // `fits` comes back false and `size` is already the floor (GA_HIGHLIGHT_NAME_MIN) — the
  // existing per-pill ellipsis (whiteSpace:nowrap + overflow:hidden + textOverflow:ellipsis on
  // the name span) absorbs the residual overflow as a last resort, never a wrap.
  const { size: nameFontSize } = useFitToWidth(containerRef, probeRowRef, {
    min: GA_HIGHLIGHT_NAME_MIN,
    max: GA_HIGHLIGHT_NAME_MAX,
    step: GA_HIGHLIGHT_FIT_STEP,
    applyToProbe: size => {
      const probeRow = probeRowRef.current;
      if (!probeRow) return;
      for (const pill of Array.from(probeRow.children) as HTMLElement[]) {
        // Each probe pill is [labelSpan, nameSpan] in that DOM order (mirrors the visible pill).
        const labelSpan = pill.firstElementChild as HTMLElement | null;
        const nameSpan = pill.lastElementChild as HTMLElement | null;
        if (labelSpan) labelSpan.style.fontSize = `${size * GA_HIGHLIGHT_LABEL_RATIO}px`;
        if (nameSpan) nameSpan.style.fontSize = `${size}px`;
      }
    },
    // Reading scrollWidth immediately after applyToProbe forces a synchronous layout, giving an
    // accurate unshrunk (nowrap, flexShrink:0) row width at each candidate size.
    measureProbe: () => probeRowRef.current?.scrollWidth ?? 0,
    deps: [pillsKey],
  });

  if (pills.length === 0) return null;

  const labelFontSize = nameFontSize * GA_HIGHLIGHT_LABEL_RATIO;

  return (
    <div
      className={animClassName}
      style={{ marginTop, width: '100%', flexShrink: 0, textAlign: isP1Layout ? 'left' : 'right' }}
    >
      <div style={eyebrowStyle}>Highlights</div>
      <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: 0 }}>
        {/* Hidden intrinsic-width probe — never painted (visibility:hidden, pointerEvents:none),
            out of layout flow (position:absolute) so it doesn't affect the visible row's height. */}
        <div
          ref={probeRowRef}
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'row',
            gap: 6,
            width: 'max-content',
          }}
        >
          {pills.map(pill => (
            <div
              key={pill.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                gap: 1,
                padding: '3px 8px',
                borderRadius: 6,
                border: `1px solid ${glassBorder.light}`,
              }}
            >
              <span style={{ fontWeight: 400, lineHeight: 1.3, whiteSpace: 'nowrap' }}>{pill.label}</span>
              <span style={{ fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap' }}>{pill.value}</span>
            </div>
          ))}
        </div>

        {/* Visible row — flexWrap:'nowrap' enforces the single-line requirement; the fitted font
            scale (computed above) keeps full card names legible without ever wrapping to a 2nd row. */}
        <div style={{
          display: 'flex',
          flexDirection: isP1Layout ? 'row' : 'row-reverse',
          gap: 6,
          flexWrap: 'nowrap',
          alignItems: 'flex-start',
        }}>
          {pills.map(pill => (
            <div
              key={pill.key}
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: isP1Layout ? 'flex-start' : 'flex-end',
                gap: 1,
                padding: '3px 8px',
                borderRadius: 6,
                background: glassBorder.subtle,
                border: `1px solid ${glassBorder.light}`,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <span style={{
                fontSize: labelFontSize,
                fontWeight: 400,
                color: glassText.muted,
                lineHeight: 1.3,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}>
                {pill.label}
              </span>
              <span style={{
                fontSize: nameFontSize,
                fontWeight: 600,
                color: glassText.primary,
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {pill.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GrandArchiveDensePanel — dense half-width compare panel for Grand Archive.
// Sections: identity (StackedCardAvatars circleSize=60) → element breakdown →
//           card types (top 5) → combined cost curve → zone composition →
//           subtypes (top 4 + more) → keywords (cap 12).
// Zone fill rendered INSIDE this panel (not VSCenter). DeckCompareOverlay
// must pass hideZoneFill={true} to VSCenter when gameId === 'grand-archive'.
// ---------------------------------------------------------------------------

export interface GrandArchiveDensePanelProps {
  side: 'p1' | 'p2';
  stats: ComparisonDeckStats | null;
  peerHasStats: boolean;
  playerName: string;
  archetype: string;
  phase: AnimPhase;
  reducedMotion: boolean;
  gaIdentitySpiritUrl?: PlayerProfile['gaIdentitySpiritUrl'];
  gaIdentityChampionUrl?: PlayerProfile['gaIdentityChampionUrl'];
  /** Shared max for the Memory/Reserve cost MiniBarCharts — computed at overlay level across both series of both players. */
  combinedCostSharedMax: number;
  /** Label union for the Memory/Reserve cost x-axis consistency. */
  combinedCostLabelUnion: string[];
  fullWidth?: boolean;
  naturalLayout?: boolean;
  placeholderText?: string;
}

export function GrandArchiveDensePanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  phase,
  reducedMotion,
  gaIdentitySpiritUrl,
  gaIdentityChampionUrl,
  combinedCostSharedMax,
  // combinedCostLabelUnion intentionally not destructured — per-series labels are derived locally
  // from memoryCostData/reserveCostData so each chart only shows its own cost range.
  fullWidth,
  naturalLayout,
  placeholderText,
}: GrandArchiveDensePanelProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const isP1 = side === 'p1';
  // naturalLayout: direction logic resolves to P1 orientation; accent stays keyed on side (isP1).
  const isP1Layout = isP1 || !!naturalLayout;
  const layoutSide: 'p1' | 'p2' = isP1Layout ? 'p1' : side;
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const accentRgba = isP1 ? p1rgba : p2rgba;
  const hasStats = stats !== null;
  const hasName = playerName.trim().length > 0;
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  // circleSize=84: overlap=round(84*0.58)=49, offset=35, containerSize=35+84=119px
  // Matches ChronoCoreDensePanel AVATAR_CIRCLE_SIZE=84 footprint.
  const AVATAR_CIRCLE_SIZE = 84;
  const AVATAR_CONTAINER_SIZE = 119;

  // Avatar block (three-state: one-deck placeholder pulse, stacked card avatars, static placeholder)
  // BACK circle = spirit: Lv0 champion from loadout IS the starting spirit per GA rules.
  // Fall back to gaIdentitySpiritUrl prop when loadout data is not yet computed.
  const spiritCardUrl = stats?.gaStartingLoadout?.levelZeroChampionUrl ?? gaIdentitySpiritUrl ?? null;
  // FRONT circle = highest-level champion URL from loadout; fall back to gaIdentityChampionUrl when absent.
  const highestChampionCardUrl = stats?.gaStartingLoadout?.highestChampionCardUrl ?? gaIdentityChampionUrl ?? null;
  const hasAnyUrl = (spiritCardUrl != null && spiritCardUrl !== '') || (highestChampionCardUrl != null && highestChampionCardUrl !== '');

  let avatarBlock: ReactNode;
  if (!hasStats && isOneDeckPlaceholder) {
    // One-deck state: pulsing border rect sized to match the 119px avatar footprint.
    avatarBlock = (
      <div
        className={reducedMotion ? undefined : 'dcao-placeholder-pulse'}
        style={{
          width: AVATAR_CONTAINER_SIZE,
          height: AVATAR_CONTAINER_SIZE,
          borderRadius: 8,
          background: glassBorder.subtle,
          border: `2px solid ${accentColor}`,
          opacity: reducedMotion ? 0.35 : undefined,
          flexShrink: 0,
        }}
      />
    );
  } else if (hasAnyUrl && hasStats) {
    // Has spirit/champion URLs and a loaded deck: stacked card avatars.
    // pilotCardUrl = BACK circle (spirit/Lv0 champion); coreCardUrl = FRONT circle (highest champion).
    avatarBlock = (
      <div className={animCls('dcao-pilot-enter')} style={{ flexShrink: 0 }}>
        <StackedCardAvatars
          pilotCardUrl={spiritCardUrl}
          coreCardUrl={highestChampionCardUrl}
          circleSize={AVATAR_CIRCLE_SIZE}
          mirror={!isP1Layout}
        />
      </div>
    );
  } else {
    // No URLs or deck not loaded: static muted placeholder rect.
    avatarBlock = (
      <div style={{
        width: AVATAR_CONTAINER_SIZE,
        height: AVATAR_CONTAINER_SIZE,
        borderRadius: 8,
        background: glassBorder.subtle,
        opacity: 0.5,
        flexShrink: 0,
      }} />
    );
  }

  // ── Per-zone payload lookups (Phase 1 contract; all fields are optional) ────
  // D86 invariant: game logic runs web-side; overlay renders from serialized payload only.
  const gaLifeCurve = stats?.gaLifeCurve ?? [];
  const gaStartingLoadout = stats?.gaStartingLoadout ?? null;
  const gaElementDistribution = stats?.gaElementDistribution ?? [];

  // Material subtypes — flat array per zone (Record<string, Array<{label,count}>>, no [0] double-index).
  // 2-col grid in half column (paired with Memory Cost in a 50/50 row); cap 6 (2 cols × 3 rows).
  const materialSubtypesRaw = stats?.subtypeDistributionsByZone?.['material'] ?? [];
  const materialSubtypesSorted = [...materialSubtypesRaw].sort((a, b) => b.count - a.count);
  const top6MaterialSubtypes = materialSubtypesSorted; // show all — no cap (CD: never +N more)
  const materialSubtypeOverflow = materialSubtypesSorted.length - top6MaterialSubtypes.length;

  // Main deck types — cap legend at 3, donut appears exactly once in the panel.
  const mainTypesRaw = stats?.typeDistributionByZone?.['main'] ?? [];
  const mainTypesSorted = [...mainTypesRaw].sort((a, b) => b.count - a.count);
  const top3MainTypes = mainTypesSorted.slice(0, 3);
  const mainTypeOverflow = mainTypesSorted.length - top3MainTypes.length;

  // Main deck card count for donut center — zoneFill entry where zoneId === 'main'.
  // D86 invariant: reads from pre-serialized payload (stats.zoneFill), not card iteration.
  const mainZoneFill = stats?.zoneFill?.find(z => z.zoneId === 'main');
  const mainDeckCount = mainZoneFill?.current ?? null;

  // Main deck subtypes — full-width row: 4-col grid, cap 8 (4 cols × 2 rows = 8 entries).
  const mainSubtypesRaw = stats?.subtypeDistributionsByZone?.['main'] ?? [];
  const mainSubtypesSorted = [...mainSubtypesRaw].sort((a, b) => b.count - a.count);
  const top8MainSubtypes = mainSubtypesSorted; // show all — no cap (CD: never +N more)
  const mainSubtypeOverflow = mainSubtypesSorted.length - top8MainSubtypes.length;

  // Keywords — from stats.keywordDistribution (GA adapter has keywordField: 'keywordBases').
  // Cap 8 for height budget; "+N more" chip shows overflow count.
  const top8Keywords = stats?.keywordDistribution
    ? [...stats.keywordDistribution].sort((a, b) => b.count - a.count).slice(0, 8)
    : [];
  const keywordOverflow = (stats?.keywordDistribution?.length ?? 0) - top8Keywords.length;

  // Memory / Reserve cost — two independent series from combinedCostCurve
  // Index 0 = material/Memory series, index 1 = main/Reserve series per the adapter's serialization order.
  const memoryCostData = stats?.combinedCostCurve?.[0]?.data ?? [];
  const reserveCostData = stats?.combinedCostCurve?.[1]?.data ?? [];
  // Per-series label sets: each chart shows only cost levels from its own series.
  // GA cost model is mutually exclusive — memory cards use 0–3, reserve cards use 2–6+ — so the
  // combined label union produces trailing empty columns on the chart whose range is narrower.
  // Bar heights still share combinedCostSharedMax for cross-player comparability.
  const memoryCostLabels = memoryCostData.map(d => d.label).sort((a, b) => Number(a) - Number(b));
  const reserveCostLabels = reserveCostData.map(d => d.label).sort((a, b) => Number(a) - Number(b));

  // Section-header eyebrow style — 14px, matching MiniHeader across all adapters.
  const eyebrowStyle: CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: glassText.disabled,
    marginBottom: 4,
    flexShrink: 0,
  };

  return (
    <div
      aria-hidden="true"
      className={animCls(isP1Layout ? 'dcao-p1-enter' : 'dcao-p2-enter')}
      style={{
        flex: fullWidth ? 1 : '0 0 680px',
        maxWidth: fullWidth ? 1400 : undefined,
        margin: fullWidth ? '0 auto' : undefined,
        height: '100%',
        position: 'relative',
        background: glass.card,
        borderRadius: 16,
        border: `1px solid ${glassBorder.light}`,
        overflow: 'hidden',
        // isP1Layout governs which edge gets the accent border and which direction the inset shadow runs.
        // accentColor/accentRgba remain keyed on isP1 (side) for color identity.
        ...(isP1Layout
          ? { borderRight: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset -4px 0 20px ${accentRgba(0.12)}` }
          : { borderLeft: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset 4px 0 20px ${accentRgba(0.12)}` }),
      }}
    >
      {/* Top accent strip */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, borderRadius: '16px 16px 0 0', background: accentColor }} />

      {/* Content column */}
      <div
        id={`dcao-content-${side}`}
        style={{
          position: 'absolute',
          top: 4,
          left: 0,
          right: 0,
          bottom: 0,
          padding: '20px 36px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          overflow: 'hidden',
        }}
      >

        {/* ── Identity zone: compact horizontal flex row (minHeight:135, mirrors ChronoCoreDensePanel) ──
            CSS order props keep slot positions invariant across P1/P2 — the parent row-reverse
            does the mirroring so DOM order never changes.
            order:0 = Avatar (outer edge, circleSize=84 → 119px stacked footprint, matches ChronoCore)
            order:1 = Names (flex:1, middle) — vertically centered beside avatar, matching CC Kellzino layout
            order:2 = Vertical divider + life curve column (inner edge, pinned right for P1 / left for P2)
            Life curve is compacted (24px numbers, 14px labels, tight gaps) so zone ≈ avatar height.
            Internal flexDirection mirrors parent so the 1px divider always sits between the names
            block and the life column for both P1 and P2. */}
        <div
          className={animCls('dcao-names-enter')}
          style={{
            display: 'flex',
            flexDirection: isP1Layout ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: 16,
            minHeight: 135,
            width: '100%',
            flexShrink: 0,
          }}
        >
          {/* order:0 — StackedCardAvatars circleSize=84 (119×119px stacked container) */}
          <div style={{ order: 0, flexShrink: 0 }}>
            {avatarBlock}
          </div>

          {/* order:1 — Player name + deck archetype (flex:1 fills remaining width).
              justifyContent:'center' vertically centers the name block beside the avatar,
              matching ChronoCore's identity layout (e.g. Kellzino centered beside pilot/core avatars). */}
          <div style={{
            order: 1,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 0,
            alignItems: isP1Layout ? 'flex-start' : 'flex-end',
            textAlign: isP1Layout ? 'left' : 'right',
          }}>
            {/* Player name (36px/700) — matches ChronoCoreDensePanel */}
            {hasName ? (
              <div style={{
                fontSize: 36,
                fontFamily: fonts.display,
                fontWeight: 700,
                color: glassText.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.0,
                marginBottom: 4,
                width: '100%',
              }}>
                {playerName}
              </div>
            ) : (
              <div style={{ fontSize: 16, fontWeight: 400, color: glassText.muted, marginBottom: 4 }}>
                {isP1 ? 'Player 1' : 'Player 2'}
              </div>
            )}
            {/* Archetype / deck name (18px/500) */}
            {archetype && (
              <div style={{
                fontSize: 18,
                fontWeight: 500,
                color: glassText.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginBottom: 3,
                width: '100%',
              }}>
                {archetype}
              </div>
            )}
            {/* Fallback when no stats */}
            {!hasStats && !archetype && (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.disabled, fontStyle: 'italic' }}>
                {hasName ? 'Awaiting deck...' : (placeholderText ?? 'Load a deck in the caster console to see stats')}
              </div>
            )}
          </div>

          {/* order:2 — Vertical life curve column, pinned to inner edge (right for P1, left for P2).
              Internal flexDirection mirrors parent row-reverse so the 1px divider always sits
              between the name block (order:1) and the life column — for both P1 and P2.
              P1 internal: [divider | gap | life-column] → divider faces names on the left.
              P2 internal (row-reverse): [life-column | gap | divider] → divider faces names on the right.
              Number font: 24px (compacted from 32px), weight 700, accentColor + STREAM_TEXT_OUTLINE.
              Label: 14px, muted, uppercase. */}
          {gaLifeCurve.length > 0 && (
            <div
              aria-hidden="true"
              style={{
                order: 2,
                display: 'flex',
                flexDirection: isP1Layout ? 'row' : 'row-reverse',
                alignItems: 'center',
                flexShrink: 0,
                gap: 12,
              }}
            >
              {/* Thin vertical divider — overlayTokens border token, spans full identity zone height */}
              <div style={{
                width: 1,
                alignSelf: 'stretch',
                background: glassBorder.medium,
                flexShrink: 0,
              }} />
              {/* Vertical life stack — one entry per GA life level (SPIRIT / Lv1 / Lv2 / Lv3).
                  Compacted to fit ~135px: 24px number + 14px label + 0 inner gap + 3 outer gap ≈
                  4×38 + 3×3 = 161px. Still slightly above strict avatar height (119px) but a
                  significant improvement over the prior 32px/~210px — FLAG for CD to eyeball. */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                alignItems: 'center',
              }}>
                {gaLifeCurve.map(entry => (
                  <div key={entry.label} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0,
                  }}>
                    <div style={{
                      fontSize: 24,
                      fontFamily: fonts.display,
                      fontWeight: 700,
                      color: accentColor,
                      lineHeight: 1,
                      textShadow: STREAM_TEXT_OUTLINE,
                    }}>
                      {entry.life}
                    </div>
                    <div style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: glassText.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}>
                      {entry.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Deck group sections: MATERIAL DECK → MAIN DECK → KEYWORDS ─────────── */}
        {hasStats ? (
          <>
            {/* ══ MATERIAL DECK ══ — 18px group header + glassBorder divider rule.
                Groups: HIGHLIGHTS + SUBTYPES | MEMORY COST BREAKDOWN (2-col 50/50). */}
            <div style={{ marginTop: 8, flexShrink: 0 }}>
              <div style={{
                fontSize: 16,
                fontFamily: fonts.display,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: glassText.secondary,
                textAlign: isP1Layout ? 'left' : 'right',
                marginBottom: 4,
              }}>
                Material Deck
              </div>
              <div style={{ height: 1, background: glassBorder.subtle }} />
            </div>

            {/* ── HIGHLIGHTS — spirit / champion / divine relic pills.
                Moved inside the Material Deck group (was top-level gaStartingLoadout block).
                Eyebrow stripped to "HIGHLIGHTS" — the group header carries "Material Deck".
                Fit-to-width font scaling (GaHighlightsRow) keeps this on a single line — see
                the component's doc comment above GrandArchiveDensePanel for the approach. */}
            <GaHighlightsRow
              pills={buildGaHighlightPills(gaStartingLoadout)}
              isP1Layout={isP1Layout}
              eyebrowStyle={eyebrowStyle}
              marginTop={4}
              animClassName={animCls('dcao-chip-enter')}
            />

            {/* ── SUBTYPES | MEMORY COST BREAKDOWN — 2-col 50/50 row.
                Pairing recovers a full-width row. Left: Material Subtypes 2-col grid cap 6
                (2 cols × 3 rows fits the ~290px half-column). Right: Memory Cost compact=true.
                P2: row-reverse mirrors the rest of the panel. */}
            {(top6MaterialSubtypes.length > 0 || memoryCostLabels.length > 0) && (
              <div style={{
                marginTop: 4,
                display: 'flex',
                flexDirection: isP1Layout ? 'row' : 'row-reverse',
                gap: 28,
                alignItems: 'flex-start',
                flexShrink: 0,
              }}>
                {/* Left ~290px: Material Subtypes 2-col grid, cap 6 */}
                <div style={{ flex: 1, minWidth: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                  {top6MaterialSubtypes.length > 0 && (
                    <>
                      <div style={eyebrowStyle}>Subtypes</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px' }}>
                        {top6MaterialSubtypes.map(entry => (
                          <div
                            key={entry.label}
                            style={{
                              height: 20,
                              display: 'flex',
                              flexDirection: isP1Layout ? 'row' : 'row-reverse',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 4,
                              minWidth: 0,
                            }}
                          >
                            <span style={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: glassText.secondary,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                              flex: 1,
                            }}>
                              {entry.label}
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 600, color: accentColor, flexShrink: 0 }}>
                              {entry.count}
                            </span>
                          </div>
                        ))}
                      </div>
                      {materialSubtypeOverflow > 0 && (
                        <div aria-hidden="true" style={{ fontSize: 14, color: glassText.muted, marginTop: 4, textAlign: isP1Layout ? 'left' : 'right' }}>
                          +{materialSubtypeOverflow} more
                        </div>
                      )}
                    </>
                  )}
                </div>
                {/* Right ~290px: Memory Cost Breakdown compact=true */}
                <div style={{ flex: 1, minWidth: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                  {memoryCostLabels.length > 0 && (
                    <>
                      <div style={eyebrowStyle}>Memory Cost Breakdown</div>
                      <div style={{ marginTop: 28 }}>
                        <MiniBarChart
                          curve={memoryCostData}
                          labelUnion={memoryCostLabels}
                          sharedMax={combinedCostSharedMax}
                          accentColor={accentColor}
                          phase={phase}
                          reverse={!isP1Layout}
                          compact={true}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ══ MAIN DECK ══ — 18px group header + glassBorder divider rule.
                Groups: TYPES | RESERVE COST BREAKDOWN (2-col) + ELEMENT BREAKDOWN + SUBTYPES. */}
            <div style={{ marginTop: 8, flexShrink: 0 }}>
              <div style={{
                fontSize: 16,
                fontFamily: fonts.display,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: glassText.secondary,
                textAlign: isP1Layout ? 'left' : 'right',
                marginBottom: 4,
              }}>
                Main Deck
              </div>
              <div style={{ height: 1, background: glassBorder.subtle }} />
            </div>

            {/* ── ELEMENT BREAKDOWN — full-width, FIRST section in Main Deck group.
                30px icons span the full panel width (~15 per row before wrap).
                D86: renders from payload only. isGAElement guard before indexing GA_ELEMENT_ICON_MAP.
                Broken image or non-GA element → empty 30×30 slot (no text fallback). P2: row-reverse. */}
            {gaElementDistribution.length > 0 && (
              <div style={{ marginTop: 4, flexShrink: 0 }}>
                <div style={{ ...eyebrowStyle, textAlign: isP1Layout ? 'left' : 'right' }}>Element Breakdown</div>
                <div style={{
                  display: 'flex',
                  flexDirection: isP1Layout ? 'row' : 'row-reverse',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'flex-start',
                }}>
                  {gaElementDistribution.map(entry => (
                    <div
                      key={entry.label}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flexShrink: 0 }}
                    >
                      {/* 30×30 icon slot (flexWrap handles overflow; full-width fits ~15 icons/row).
                          isGAElement inline-narrowing: TypeScript narrows entry.label → GAElement. */}
                      <div style={{ width: 30, height: 30, position: 'relative', flexShrink: 0 }}>
                        {isGAElement(entry.label) ? (
                          <img
                            src={GA_ELEMENT_ICON_MAP[entry.label]}
                            width={30}
                            height={30}
                            alt=""
                            style={{ display: 'block' }}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : null}
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary }}>
                        {entry.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── TYPES | RESERVE COST BREAKDOWN — 2-col paired (both compact items in Main group).
                Types donut: size=72, legend=side, center=mainDeckCount.
                Reserve Cost: compact=true; combinedCostCurve[1] (main zone series). */}
            <div style={{
              marginTop: 4,
              display: 'flex',
              flexDirection: isP1Layout ? 'row' : 'row-reverse',
              gap: 28,
              alignItems: 'flex-start',
              flexShrink: 0,
            }}>
              {/* Left ~290px: Types donut */}
              <div style={{ flex: 1, minWidth: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                <div style={eyebrowStyle}>Types</div>
                {top3MainTypes.length > 0 ? (
                  <>
                    <DonutTypeChart
                      dist={top3MainTypes}
                      side={layoutSide}
                      phase={phase}
                      size={72}
                      legendPosition="side"
                      showTypePercentages={false}
                      centerValue={mainDeckCount}
                    />
                    {mainTypeOverflow > 0 && (
                      <div aria-hidden="true" style={{ fontSize: 14, color: glassText.muted, marginTop: 4, textAlign: isP1Layout ? 'left' : 'right' }}>
                        +{mainTypeOverflow} more
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 14, color: glassText.disabled, fontStyle: 'italic' }}>—</div>
                )}
              </div>
              {/* Right ~290px: Reserve Cost Breakdown compact=true — combinedCostCurve[1] (main zone). */}
              <div style={{ flex: 1, minWidth: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                <div style={eyebrowStyle}>Reserve Cost Breakdown</div>
                <div style={{ marginTop: 28 }}>
                  <MiniBarChart
                    curve={reserveCostData}
                    labelUnion={reserveCostLabels}
                    sharedMax={combinedCostSharedMax}
                    accentColor={accentColor}
                    phase={phase}
                    reverse={!isP1Layout}
                    compact={true}
                  />
                </div>
              </div>
            </div>

            {/* ── SUBTYPES — full-width, 4-col grid, cap 8 (4 cols × 2 rows = 8 entries).
                P2: textAlign right, flexDirection row-reverse per entry row. */}
            {top8MainSubtypes.length > 0 && (
              <div style={{ marginTop: 4, flexShrink: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                <div style={eyebrowStyle}>Subtypes</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '4px 8px' }}>
                  {top8MainSubtypes.map(entry => (
                    <div
                      key={entry.label}
                      style={{
                        height: 20,
                        display: 'flex',
                        flexDirection: isP1Layout ? 'row' : 'row-reverse',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 4,
                        minWidth: 0,
                      }}
                    >
                      <span style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: glassText.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        flex: 1,
                      }}>
                        {entry.label}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: accentColor, flexShrink: 0 }}>
                        {entry.count}
                      </span>
                    </div>
                  ))}
                </div>
                {mainSubtypeOverflow > 0 && (
                  <div aria-hidden="true" style={{ fontSize: 14, color: glassText.muted, marginTop: 2, textAlign: isP1Layout ? 'left' : 'right' }}>
                    +{mainSubtypeOverflow} more
                  </div>
                )}
              </div>
            )}

            {/* ── KEYWORDS — outside both deck group headers (game-wide, not zone-specific).
                GA keywordField: 'keywordBases'; distribution IS populated.
                top8Keywords sorted desc; keywordOverflow: "+N more" chip below. */}
            {top8Keywords.length > 0 && (
              <div style={{ marginTop: 6, flexShrink: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                <div style={eyebrowStyle}>Keywords</div>
                <KeywordBubbleCloud
                  keywords={top8Keywords}
                  side={layoutSide}
                  accentColor={accentColor}
                  phase={phase}
                  isEntering={isEntering}
                  startDelay={860}
                />
                {keywordOverflow > 0 && (
                  <div aria-hidden="true" style={{ fontSize: 14, color: glassText.muted, marginTop: 4 }}>
                    +{keywordOverflow} more
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <DensePanelPlaceholder hasName={hasName} placeholderText={placeholderText} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GrandArchiveRailPanel — condensed profile rail for Grand Archive.
// Rail container: 440px wide × 880px tall (25% of 1760px inner content area;
// 1000px glass card − 100px top strip − 20px vertical padding).
// D95 Round-5: Aligned to GrandArchiveDensePanel structure. Identity zone now matches dense
// (alignItems:center, minHeight:135, gap:16; Highlights moved out of names col to Material group).
// Life curve shows ALL levels at 20px numbers (was Spirit+highest at 24px).
// Avatar: BACK=gaStartingLoadout.levelZeroChampionUrl??gaIdentitySpiritUrl,
//         FRONT=gaStartingLoadout.highestChampionCardUrl??gaIdentityChampionUrl
// Sections (MATERIAL DECK group → MAIN DECK group → KEYWORDS):
//   MATERIAL DECK: HIGHLIGHTS + SUBTYPES (MiniHorizontalBars ≤4) | MEMORY COST BREAKDOWN (compact)
//   MAIN DECK: TYPES (size=56) | ELEMENT BREAKDOWN (32px flexWrap) + SUBTYPES (2-col ≤6) | RESERVE COST (compact)
//   KEYWORDS: cap 8, compact bubbles
// P2 mirroring: every eyebrow/label/count uses textAlign: isP1Layout ? 'left' : 'right'.
// ---------------------------------------------------------------------------

export type GrandArchiveRailPanelProps = Omit<GrandArchiveDensePanelProps, 'fullWidth' | 'naturalLayout'>;

export function GrandArchiveRailPanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  phase,
  reducedMotion,
  gaIdentitySpiritUrl,
  gaIdentityChampionUrl,
  combinedCostSharedMax,
  // combinedCostLabelUnion intentionally not destructured — per-series labels are derived locally.
  placeholderText,
}: GrandArchiveRailPanelProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const isP1 = side === 'p1';
  // P2 mirroring: isP1Layout drives flexDirection row/row-reverse and sub-component side/reverse.
  // Rail panels have no naturalLayout prop; mirroring follows side directly.
  const isP1Layout = isP1;
  const layoutSide: 'p1' | 'p2' = isP1Layout ? 'p1' : side;
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const hasStats = stats !== null;
  const hasName = playerName.trim().length > 0;
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  // circleSize=64 per Phase 4 spec.
  // Footprint: overlap=Math.round(64*0.58)=37; offset=64-37=27; containerSize=27+64=91px (~85px est.).
  const AVATAR_CIRCLE_SIZE = 64;
  const AVATAR_CONTAINER_SIZE = 91;
  // BACK circle = spirit: Lv0 champion from loadout IS the starting spirit per GA rules.
  // Fall back to gaIdentitySpiritUrl prop when loadout data is not yet computed.
  const spiritCardUrl = stats?.gaStartingLoadout?.levelZeroChampionUrl ?? gaIdentitySpiritUrl ?? null;
  // FRONT circle = highest-level champion URL from loadout; fall back to gaIdentityChampionUrl when absent.
  const highestChampionCardUrl = stats?.gaStartingLoadout?.highestChampionCardUrl ?? gaIdentityChampionUrl ?? null;
  const hasAnyUrl = (spiritCardUrl != null && spiritCardUrl !== '') || (highestChampionCardUrl != null && highestChampionCardUrl !== '');

  // Avatar block (three-state: one-deck placeholder pulse, stacked card avatars, static placeholder)
  let avatarBlock: ReactNode;
  if (!hasStats && isOneDeckPlaceholder) {
    // One-deck state: pulsing border rect sized to match the 91px avatar footprint.
    avatarBlock = (
      <div
        className={reducedMotion ? undefined : 'dcao-placeholder-pulse'}
        style={{
          width: AVATAR_CONTAINER_SIZE,
          height: AVATAR_CONTAINER_SIZE,
          borderRadius: 8,
          background: glassBorder.subtle,
          border: `2px solid ${accentColor}`,
          opacity: reducedMotion ? 0.35 : undefined,
          flexShrink: 0,
        }}
      />
    );
  } else if (hasAnyUrl && hasStats) {
    // Has spirit/champion URLs and a loaded deck: stacked card avatars.
    // pilotCardUrl = BACK circle (spirit/Lv0 champion); coreCardUrl = FRONT circle (highest champion).
    avatarBlock = (
      <div className={animCls('dcao-pilot-enter')} style={{ flexShrink: 0 }}>
        <StackedCardAvatars
          pilotCardUrl={spiritCardUrl}
          coreCardUrl={highestChampionCardUrl}
          circleSize={AVATAR_CIRCLE_SIZE}
          mirror={!isP1Layout}
        />
      </div>
    );
  } else {
    // No URLs or deck not loaded: static muted placeholder rect.
    avatarBlock = (
      <div style={{
        width: AVATAR_CONTAINER_SIZE,
        height: AVATAR_CONTAINER_SIZE,
        borderRadius: 8,
        background: glassBorder.subtle,
        opacity: 0.5,
        flexShrink: 0,
      }} />
    );
  }

  // ── Per-zone payload lookups ────────────────────────────────────────────────
  // D86 invariant: game logic runs web-side; overlay renders from serialized payload only.
  const gaLifeCurveAll = stats?.gaLifeCurve ?? [];
  // Rail: show ALL present life-curve levels (Spirit + Lv1/Lv2/Lv3) — was Spirit+highest only.
  // Number font reduced to 20px (from 24px) so 4 entries fit: 4×(20+14) + 3×3 = 145px.
  // Identity zone minHeight:135 expands to 145px in the 4-level case — reported for CD review.

  const gaStartingLoadout = stats?.gaStartingLoadout ?? null;
  const gaElementDistribution = stats?.gaElementDistribution ?? [];

  // Main deck card count for donut center — zoneFill entry where zoneId === 'main'.
  // D86 invariant: reads from pre-serialized payload (stats.zoneFill), not card iteration.
  const mainZoneFill = stats?.zoneFill?.find(z => z.zoneId === 'main');
  const mainDeckCount = mainZoneFill?.current ?? null;

  // Keywords — GA keywordField: 'keywordBases'. Rail cap: 8 (vs 12 in dense); compact=true
  // to pack more rows into the available vertical space at 440px width.
  const top8Keywords = stats?.keywordDistribution
    ? [...stats.keywordDistribution].sort((a, b) => b.count - a.count).slice(0, 8)
    : [];
  const keywordOverflow = (stats?.keywordDistribution?.length ?? 0) - top8Keywords.length;

  // Material subtypes — 2-col grid in half column (paired with Memory Cost); cap 6 (2 cols × 3 rows).
  const materialSubtypesRaw = stats?.subtypeDistributionsByZone?.['material'] ?? [];
  const materialSubtypesSorted = [...materialSubtypesRaw].sort((a, b) => b.count - a.count);
  const top6MaterialSubtypes = materialSubtypesSorted; // show all — no cap (CD: never +N more)
  const materialSubtypeOverflow = materialSubtypesSorted.length - top6MaterialSubtypes.length;

  // Main deck types — cap legend at 3 (same as dense), donut size=72. Collapsible tier.
  const mainTypesRaw = stats?.typeDistributionByZone?.['main'] ?? [];
  const mainTypesSorted = [...mainTypesRaw].sort((a, b) => b.count - a.count);
  const top3MainTypes = mainTypesSorted.slice(0, 3);
  const mainTypeOverflow = mainTypesSorted.length - top3MainTypes.length;

  // Main deck subtypes — full-width row: 4-col text grid, cap 12 (4 cols × 3 rows = 12 entries).
  // Was 2-col cap 6 in a half-column; full-width restores wider grid.
  const mainSubtypesRaw = stats?.subtypeDistributionsByZone?.['main'] ?? [];
  const mainSubtypesSorted = [...mainSubtypesRaw].sort((a, b) => b.count - a.count);
  const top12MainSubtypes = mainSubtypesSorted; // show all — no cap (CD: never +N more)
  const mainSubtypeOverflow = mainSubtypesSorted.length - top12MainSubtypes.length;

  // Memory / Reserve cost — two independent series from combinedCostCurve. Must-have tier.
  const memoryCostData = stats?.combinedCostCurve?.[0]?.data ?? [];
  const reserveCostData = stats?.combinedCostCurve?.[1]?.data ?? [];
  // Per-series label sets: trim empty columns caused by the combined cross-series label union.
  // Same rationale as GrandArchiveDensePanel; bar heights still share combinedCostSharedMax.
  const memoryCostLabels = memoryCostData.map(d => d.label).sort((a, b) => Number(a) - Number(b));
  const reserveCostLabels = reserveCostData.map(d => d.label).sort((a, b) => Number(a) - Number(b));

  // Section-header eyebrow style — 14px, matching GrandArchiveDensePanel and MiniHeader.
  const eyebrowStyle: CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: glassText.disabled,
    marginBottom: 4,
    flexShrink: 0,
  };

  return (
    <div
      aria-hidden="true"
      style={{ flex: 1, height: '100%', position: 'relative', background: glass.card, borderRadius: 16, border: `1px solid ${glassBorder.light}`, overflow: 'hidden' }}
    >
      {/* Top accent strip */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: accentColor }} />

      {/* Content column */}
      <div
        id={`dcao-content-${side}`}
        style={{ position: 'absolute', top: 4, left: 0, right: 0, bottom: 0, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', overflow: 'hidden' }}
      >

        {/* ── Identity zone: horizontal flex row matching GrandArchiveDensePanel structure.
            Differences from prior rail: alignItems 'flex-start'→'center', minHeight 105→135,
            gap 12→16, Highlights pills removed from names col (now a dedicated section below).
            order:0 = Avatar | order:1 = Name + archetype (flex:1) | order:2 = Life curve (right-pinned).
            Life curve now shows ALL levels (gaLifeCurveAll) at 20px numbers (down from 24px)
            so 4 entries at 145px fit within the 135px minHeight (div expands to accommodate). */}
        <div
          className={animCls('dcao-names-enter')}
          style={{
            display: 'flex',
            flexDirection: isP1Layout ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: 16,
            minHeight: 135,
            width: '100%',
            flexShrink: 0,
          }}
        >
          {/* order:0 — StackedCardAvatars circleSize=64 (91px container); structure matches dense panel. */}
          <div style={{ order: 0, flexShrink: 0 }}>
            {avatarBlock}
          </div>

          {/* order:1 — Player name + deck archetype only (Highlights moved to Material Deck section below). */}
          <div style={{
            order: 1,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 0,
            alignItems: isP1Layout ? 'flex-start' : 'flex-end',
            textAlign: isP1Layout ? 'left' : 'right',
          }}>
            {/* Player name (24px/700) */}
            {hasName ? (
              <div style={{
                fontSize: 24,
                fontFamily: fonts.display,
                fontWeight: 700,
                color: glassText.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.0,
                marginBottom: 4,
                width: '100%',
              }}>
                {playerName}
              </div>
            ) : (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.muted, marginBottom: 4 }}>
                {isP1 ? 'Player 1' : 'Player 2'}
              </div>
            )}
            {/* Archetype / deck name (14px/500) */}
            {archetype && (
              <div style={{
                fontSize: 14,
                fontWeight: 500,
                color: glassText.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}>
                {archetype}
              </div>
            )}
            {/* Fallback when no stats */}
            {!hasStats && !archetype && (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.disabled, fontStyle: 'italic' }}>
                {hasName ? 'Awaiting deck...' : 'No deck loaded'}
              </div>
            )}
          </div>

          {/* order:2 — GA life curve column, pinned to inner edge (right for P1, left for P2).
              ALL levels rendered (Spirit + Lv1/Lv2/Lv3); number font 20px (from 24px).
              Internal flexDirection mirrors parent so divider always faces the names block.
              Number: 20px/700/accentColor + STREAM_TEXT_OUTLINE. Label: 14px/600/muted/uppercase. */}
          {gaLifeCurveAll.length > 0 && (
            <div
              aria-hidden="true"
              style={{
                order: 2,
                display: 'flex',
                flexDirection: isP1Layout ? 'row' : 'row-reverse',
                alignItems: 'center',
                flexShrink: 0,
                gap: 12,
              }}
            >
              <div style={{ width: 1, alignSelf: 'stretch', background: glassBorder.medium, flexShrink: 0 }} />
              {/* All life levels — 20px number so 4 entries (145px) fit the identity zone. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                {gaLifeCurveAll.map(entry => (
                  <div key={entry.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                    <div style={{
                      fontSize: 20,
                      fontFamily: fonts.display,
                      fontWeight: 700,
                      color: accentColor,
                      lineHeight: 1,
                      textShadow: STREAM_TEXT_OUTLINE,
                    }}>
                      {entry.life}
                    </div>
                    <div style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: glassText.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}>
                      {entry.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Deck group sections: MATERIAL DECK → MAIN DECK → KEYWORDS ─────────── */}
        {hasStats ? (
          <>
            {/* ══ MATERIAL DECK ══ — 18px group header + glassBorder divider rule. */}
            <div style={{ marginTop: 10, flexShrink: 0 }}>
              <div style={{
                fontSize: 16,
                fontFamily: fonts.display,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: glassText.secondary,
                textAlign: isP1Layout ? 'left' : 'right',
                marginBottom: 5,
              }}>
                Material Deck
              </div>
              <div style={{ height: 1, background: glassBorder.subtle }} />
            </div>

            {/* ── HIGHLIGHTS — spirit / champion / divine relic pills.
                Moved from inside the names column to this dedicated section under the group header.
                Same pill structure as GrandArchiveDensePanel — fit-to-width scaled via
                GaHighlightsRow (matters more here: the rail's content column is much narrower). */}
            <GaHighlightsRow
              pills={buildGaHighlightPills(gaStartingLoadout)}
              isP1Layout={isP1Layout}
              eyebrowStyle={eyebrowStyle}
              marginTop={6}
              animClassName={animCls('dcao-chip-enter')}
            />

            {/* ── SUBTYPES | MEMORY COST BREAKDOWN — 2-col 50/50 row.
                Pairing recovers a full-width row. Left: Material Subtypes 2-col grid cap 6
                (2 cols × 3 rows, fits the ~186px half-column). Right: Memory Cost compact=true.
                P2: row-reverse mirrors the rest of the panel. */}
            {(top6MaterialSubtypes.length > 0 || memoryCostLabels.length > 0) && (
              <div style={{
                marginTop: 6,
                display: 'flex',
                flexDirection: isP1Layout ? 'row' : 'row-reverse',
                gap: 20,
                alignItems: 'flex-start',
                flexShrink: 0,
              }}>
                {/* Left ~186px: Material Subtypes 2-col grid, cap 6 */}
                <div style={{ flex: 1, minWidth: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                  {top6MaterialSubtypes.length > 0 && (
                    <>
                      <div style={eyebrowStyle}>Subtypes</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px' }}>
                        {top6MaterialSubtypes.map(entry => (
                          <div
                            key={entry.label}
                            style={{
                              height: 20,
                              display: 'flex',
                              flexDirection: isP1Layout ? 'row' : 'row-reverse',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 4,
                              minWidth: 0,
                            }}
                          >
                            <span style={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: glassText.secondary,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                              flex: 1,
                            }}>
                              {entry.label}
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 600, color: accentColor, flexShrink: 0 }}>
                              {entry.count}
                            </span>
                          </div>
                        ))}
                      </div>
                      {materialSubtypeOverflow > 0 && (
                        <div aria-hidden="true" style={{ fontSize: 14, color: glassText.muted, marginTop: 4, textAlign: isP1Layout ? 'left' : 'right' }}>
                          +{materialSubtypeOverflow} more
                        </div>
                      )}
                    </>
                  )}
                </div>
                {/* Right ~186px: Memory Cost Breakdown compact=true */}
                <div style={{ flex: 1, minWidth: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                  {memoryCostLabels.length > 0 && (
                    <>
                      <div style={eyebrowStyle}>Memory Cost Breakdown</div>
                      <div style={{ marginTop: 28 }}>
                        <MiniBarChart
                          curve={memoryCostData}
                          labelUnion={memoryCostLabels}
                          sharedMax={combinedCostSharedMax}
                          accentColor={accentColor}
                          phase={phase}
                          reverse={!isP1Layout}
                          compact={true}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ══ MAIN DECK ══ — 18px group header + glassBorder divider rule. */}
            <div style={{ marginTop: 10, flexShrink: 0 }}>
              <div style={{
                fontSize: 16,
                fontFamily: fonts.display,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: glassText.secondary,
                textAlign: isP1Layout ? 'left' : 'right',
                marginBottom: 5,
              }}>
                Main Deck
              </div>
              <div style={{ height: 1, background: glassBorder.subtle }} />
            </div>

            {/* ── ELEMENT BREAKDOWN — full-width, FIRST section in Main Deck group.
                30px icons span the full rail width (~9 per row); flexWrap handles overflow.
                isGAElement guard before indexing GA_ELEMENT_ICON_MAP. Broken image or
                non-GA element → empty 30×30 slot (no text fallback). */}
            {gaElementDistribution.length > 0 && (
              <div style={{ marginTop: 6, flexShrink: 0 }}>
                <div style={{ ...eyebrowStyle, textAlign: isP1Layout ? 'left' : 'right' }}>Element Breakdown</div>
                <div style={{
                  display: 'flex',
                  flexDirection: isP1Layout ? 'row' : 'row-reverse',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'flex-start',
                }}>
                  {gaElementDistribution.map(entry => (
                    <div
                      key={entry.label}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flexShrink: 0 }}
                    >
                      {/* 30×30 icon slot. isGAElement inline-narrowing → GAElement. */}
                      <div style={{ width: 30, height: 30, position: 'relative', flexShrink: 0 }}>
                        {isGAElement(entry.label) ? (
                          <img
                            src={GA_ELEMENT_ICON_MAP[entry.label]}
                            width={30}
                            height={30}
                            alt=""
                            style={{ display: 'block' }}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : null}
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary }}>
                        {entry.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── TYPES | RESERVE COST BREAKDOWN — 2-col paired (compact items in Main group).
                Pairing recovers height for the full-width element and subtypes rows below.
                Types donut: size=56 for rail column. Reserve Cost: compact=true for rail budget. */}
            <div style={{
              marginTop: 6,
              display: 'flex',
              flexDirection: isP1Layout ? 'row' : 'row-reverse',
              gap: 20,
              alignItems: 'flex-start',
              flexShrink: 0,
            }}>
              {/* Left ~186px: Types donut (size=56 for rail column) */}
              <div style={{ flex: 1, minWidth: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                <div style={eyebrowStyle}>Types</div>
                {top3MainTypes.length > 0 ? (
                  <>
                    <DonutTypeChart
                      dist={top3MainTypes}
                      side={layoutSide}
                      phase={phase}
                      size={56}
                      legendPosition="side"
                      showTypePercentages={false}
                      centerValue={mainDeckCount}
                    />
                    {mainTypeOverflow > 0 && (
                      <div aria-hidden="true" style={{ fontSize: 14, color: glassText.muted, marginTop: 4, textAlign: isP1Layout ? 'left' : 'right' }}>
                        +{mainTypeOverflow} more
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 14, color: glassText.disabled, fontStyle: 'italic' }}>—</div>
                )}
              </div>
              {/* Right ~186px: Reserve Cost Breakdown — combinedCostCurve[1] (main zone series).
                  compact=true suits rail height budget; marginTop:28 clears count labels above bars. */}
              <div style={{ flex: 1, minWidth: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                <div style={eyebrowStyle}>Reserve Cost Breakdown</div>
                <div style={{ marginTop: 28 }}>
                  <MiniBarChart
                    curve={reserveCostData}
                    labelUnion={reserveCostLabels}
                    sharedMax={combinedCostSharedMax}
                    accentColor={accentColor}
                    phase={phase}
                    reverse={!isP1Layout}
                    compact={true}
                  />
                </div>
              </div>
            </div>

            {/* ── SUBTYPES — full-width, 4-col text grid, cap 12 (4 cols × 3 rows = 12 entries).
                Was 2-col cap 6 in half-column; full-width restores wider grid. P2: row-reverse per entry. */}
            {top12MainSubtypes.length > 0 && (
              <div style={{ marginTop: 6, flexShrink: 0, textAlign: isP1Layout ? 'left' : 'right' }}>
                <div style={eyebrowStyle}>Subtypes</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '4px 8px' }}>
                  {top12MainSubtypes.map(entry => (
                    <div
                      key={entry.label}
                      style={{
                        height: 20,
                        display: 'flex',
                        flexDirection: isP1Layout ? 'row' : 'row-reverse',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 4,
                        minWidth: 0,
                      }}
                    >
                      <span style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: glassText.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        flex: 1,
                      }}>
                        {entry.label}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: accentColor, flexShrink: 0 }}>
                        {entry.count}
                      </span>
                    </div>
                  ))}
                </div>
                {mainSubtypeOverflow > 0 && (
                  <div aria-hidden="true" style={{ fontSize: 14, color: glassText.muted, marginTop: 2, textAlign: isP1Layout ? 'left' : 'right' }}>
                    +{mainSubtypeOverflow} more
                  </div>
                )}
              </div>
            )}

            {/* ── KEYWORDS — outside both deck group headers (game-wide, not zone-specific).
                Rail cap: 8 (vs 12 dense); compact=true for narrower bubbles. */}
            {top8Keywords.length > 0 && (
              <div style={{ marginTop: 8, flexShrink: 0 }}>
                <div style={{ ...eyebrowStyle, textAlign: isP1Layout ? 'left' : 'right' }}>Keywords</div>
                <KeywordBubbleCloud
                  keywords={top8Keywords}
                  side={layoutSide}
                  accentColor={accentColor}
                  phase={phase}
                  isEntering={isEntering}
                  startDelay={860}
                  compact={true}
                />
                {keywordOverflow > 0 && (
                  <div aria-hidden="true" style={{ fontSize: 14, color: glassText.muted, marginTop: 4, textAlign: isP1Layout ? 'left' : 'right' }}>
                    +{keywordOverflow} more
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <DensePanelPlaceholder hasName={hasName} placeholderText={placeholderText} />
        )}
      </div>
    </div>
  );
}

